import { useEffect, useState } from "react";
import { DEFAULT_LANGUAGE } from "@/i18n";
import { getComputedLanguage, usePreferencesStore } from "@/lib/store/preferences";

export type LanguagePreference = "system" | Language;

/**
 * Which language the app speaks, and which one the employee asked for.
 *
 * The store already resolves and applies the choice — `setLanguage` calls `enforceLanguage`, which
 * asks the device for its locale when the preference is `system` and hands the result to
 * `i18n.changeLanguage`. This hook exists for the screen that shows it: a picker has to say which
 * option is selected _and_, when that option is "system", which language the device actually
 * resolved to. Otherwise "System language" is a row that tells you nothing.
 *
 * `system` is the default on purpose. A phone's language is a decision its owner already made once,
 * for every app at once, and following it is what the platform expects. The explicit options are
 * for the employees who want this app to disagree with that — someone working in a Dutch team on an
 * English phone, or the other way round.
 */
export function useLanguage() {
  const preference = usePreferencesStore((state) => state.language);
  const setPreference = usePreferencesStore((state) => state.setLanguage);
  const [system, setSystem] = useState<Language>(DEFAULT_LANGUAGE);

  // What the device resolves to, which is only knowable asynchronously: Capacitor's Device plugin
  // is a native call. Until it answers, the default is the honest thing to show — it is also what
  // `getComputedLanguage` falls back to for a locale the app has no bundle for.
  useEffect(() => {
    let active = true;

    void getComputedLanguage("system").then((language) => {
      if (active) {
        setSystem(language);
      }
    });

    return () => {
      active = false;
    };
  }, []);

  return {
    /** What the employee chose: a language, or `system` to follow the device. */
    preference,
    /** The language the device resolves to, for showing what `system` currently means. */
    system,
    setPreference,
  };
}
